import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, MapPin, CheckCircle, Banknote, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const buyNowData = location.state?.buyNow;
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentFailReason, setPaymentFailReason] = useState('cancelled'); // 'cancelled' | 'declined'
  const [completedOrderId, setCompletedOrderId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const razorpayOpenRef = useRef(false);
  const [address, setAddress] = useState({ name: '', line1: '', line2: '', city: '', state: '', pincode: '', phone: '' });
  const [pincodeStatus, setPincodeStatus] = useState('idle'); // idle | loading | success | error
  const [pincodeError, setPincodeError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [postOffices, setPostOffices] = useState([]);

  const isBuyNow = !!buyNowData;
  const codAvailable = isBuyNow ? buyNowData.product?.cod_available : cart.items.every(i => i.product?.cod_available);

  useEffect(() => {
    if (isBuyNow) {
      const p = buyNowData.product;
      setCart({ items: [{ product_id: p.id, quantity: buyNowData.quantity, product: p }], total: p.price * buyNowData.quantity });
    } else {
      axios.get(`${API}/cart`, { withCredentials: true }).then(r => { setCart(r.data); if (r.data.items.length === 0) navigate('/cart'); }).catch(() => navigate('/cart'));
    }
  }, [navigate, isBuyNow, buyNowData]);

  useEffect(() => {
    if (user) {
      // Strip +91 or + prefix since the input field already shows a +91 label
      let phone = user.phone || '';
      if (phone.startsWith('+91')) phone = phone.slice(3);
      else if (phone.startsWith('+')) phone = phone.slice(1);
      setAddress(prev => ({ ...prev, name: user.name || '', phone }));
    }
  }, [user]);

  const lookupPincode = useCallback(async (pin) => {
    if (pin.length !== 6) {
      setPincodeStatus('idle');
      setPincodeError('');
      setPostOffices([]);
      setAddress(prev => ({ ...prev, city: '', state: '' }));
      return;
    }
    setPincodeStatus('loading');
    setPincodeError('');
    try {
      const res = await fetch(`${API}/pincode/${pin}`);
      const json = await res.json();
      if (json[0]?.Status === 'Success' && json[0]?.PostOffice?.length > 0) {
        const po = json[0].PostOffice;
        const city = po[0].District;
        const state = po[0].State;
        setAddress(prev => ({ ...prev, city, state }));
        setPostOffices(po.map(p => p.Name));
        setPincodeStatus('success');
      } else {
        setPincodeStatus('error');
        setPincodeError('Invalid PIN code. Please check and try again.');
        setAddress(prev => ({ ...prev, city: '', state: '' }));
        setPostOffices([]);
      }
    } catch {
      setPincodeStatus('error');
      setPincodeError('Could not verify PIN code. Please fill city & state manually.');
    }
  }, []);

  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setAddress(prev => ({ ...prev, pincode: val }));
    lookupPincode(val);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setAddress(prev => ({ ...prev, phone: val }));
    if (val.length > 0 && val.length < 10) {
      setPhoneError('Enter a valid 10-digit mobile number');
    } else if (val.length === 10 && !/^[6-9]\d{9}$/.test(val)) {
      setPhoneError('Mobile number must start with 6, 7, 8, or 9');
    } else {
      setPhoneError('');
    }
  };

  const loadRazorpay = () => new Promise(r => { if (window.Razorpay) { r(true); return; } const s = document.createElement('script'); s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.onload = () => r(true); s.onerror = () => r(false); document.body.appendChild(s); });

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!address.name || !address.line1 || !address.city || !address.pincode) { toast.error('Fill all required fields'); return; }
    if (address.pincode.length !== 6) { toast.error('Enter a valid 6-digit PIN code'); return; }
    if (pincodeStatus === 'error') { toast.error('Please enter a valid PIN code'); return; }
    if (!/^[6-9]\d{9}$/.test(address.phone)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const endpoint = isBuyNow ? `${API}/orders/buy-now` : `${API}/orders`;
      const payload = isBuyNow
        ? { product_id: buyNowData.product_id, quantity: buyNowData.quantity, shipping_address: address, phone: address.phone, payment_method: paymentMethod }
        : { shipping_address: address, phone: address.phone, payment_method: paymentMethod };
      const { data } = await axios.post(endpoint, payload, { withCredentials: true });

      if (paymentMethod === 'cod' || data.is_demo || data.status === 'confirmed' || data.status === 'demo_paid') {
        setCompletedOrderId(data.order_id);
        setOrderComplete(true);
        toast.success(paymentMethod === 'cod' ? 'COD order placed!' : 'Order placed!');
        setLoading(false);
        return;
      }

      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed'); setLoading(false); return; }

      // Called when user explicitly cancels (closes modal via ✕ or back)
      const handleCancelled = async () => {
        razorpayOpenRef.current = false;
        try { await axios.post(`${API}/orders/${data.order_id}/payment-failed`, {}, { withCredentials: true }); } catch {}
        setLoading(false);
        setPaymentFailReason('cancelled');
        setPaymentFailed(true);
      };

      // Called when bank / payment gateway declines the payment
      const handleBankDeclined = async () => {
        razorpayOpenRef.current = false;
        try { await axios.post(`${API}/orders/${data.order_id}/payment-failed`, {}, { withCredentials: true }); } catch {}
        setLoading(false);
        setPaymentFailReason('declined');
        setPaymentFailed(true);
      };

      const options = {
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency,
        name: 'Pets Valley', description: 'Order Payment', order_id: data.razorpay_order_id,
        handler: async (response) => {
          razorpayOpenRef.current = false;
          try {
            await axios.post(`${API}/orders/verify`, { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature, order_id: data.order_id }, { withCredentials: true });
            setCompletedOrderId(data.order_id); setOrderComplete(true); toast.success('Payment successful!');
          } catch { toast.error('Verification failed'); }
          setLoading(false);
        },
        prefill: { name: address.name, email: user?.email, contact: address.phone },
        theme: { color: '#C45E2A' },
        modal: { ondismiss: handleCancelled }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', handleBankDeclined);
      razorpayOpenRef.current = true;
      rzp.open();
      // NOTE: rzp.open() is non-blocking. Do NOT setLoading(false) here —
      // it is handled inside handler / handleCancelled / handleBankDeclined.
      return; // skip the finally setLoading(false)
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); setLoading(false); }
  };

  if (orderComplete) return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="order-success">
      <div className="text-center max-w-md">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h1 className="font-heading text-3xl font-bold text-dark-brown mb-3">Order Placed!</h1>
        <p className="font-body text-light-brown mb-2">{paymentMethod === 'cod' ? 'Your COD order has been confirmed.' : 'Your order has been placed.'}</p>
        <p className="font-body text-sm text-mid-brown mb-6">Order ID: <strong>{completedOrderId}</strong></p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate('/orders')} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full px-6" data-testid="view-orders-btn">View Orders</Button>
          <Button onClick={() => navigate('/products')} variant="outline" className="border-rust text-rust font-body rounded-full px-6">Continue Shopping</Button>
        </div>
      </div>
    </div>
  );

  if (paymentFailed) return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="payment-failed">
      <div className="text-center max-w-md bg-white rounded-2xl border border-red-200 p-8 shadow-sm">
        <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-bold text-dark-brown mb-2">Payment Failed</h1>
        <p className="font-body text-lg font-semibold text-red-600 mb-3">Order Not Placed</p>
        {paymentFailReason === 'declined' ? (
          <p className="font-body text-sm text-light-brown mb-4">
            Your payment could not be completed. It was <strong>declined by your bank</strong> — this may be due to insufficient balance, daily limits, or your bank blocking the transaction.
          </p>
        ) : (
          <p className="font-body text-sm text-light-brown mb-4">
            Your transaction could not be completed. The payment was cancelled.
          </p>
        )}
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>💡</span>
          <p className="font-body text-xs" style={{ color: '#1E40AF', lineHeight: 1.6 }}>
            {paymentFailReason === 'declined'
              ? <>Please check your <strong>balance, UPI limits, or try a different payment method</strong>. If any amount was deducted, it will be <strong>automatically refunded within 5-7 business days</strong>.</>
              : <>If any amount was deducted from your account, it will be <strong>automatically refunded within 5-7 business days</strong>. You can contact us for help.</>
            }
          </p>
        </div>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => setPaymentFailed(false)} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full px-6">Retry Payment</Button>
          <Button onClick={() => navigate('/orders')} variant="outline" className="border-rust text-rust font-body rounded-full px-6">View Orders</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="checkout-page">
      {loading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,18,9,0.5)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: '20px', padding: '40px 48px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxWidth: '400px', width: '90%' }}>
            <div className="spinner" style={{ width: '48px', height: '48px', margin: '0 auto 20px' }}></div>
            <p className="font-body text-xl font-bold text-dark-brown" style={{ marginBottom: '8px' }}>Processing your order...</p>
            <p className="font-body text-sm text-light-brown" style={{ marginBottom: '20px' }}>Please wait while we confirm your order</p>
            <div style={{ background: '#FFF3CD', border: '1px solid #FFECB5', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
              <p className="font-body text-xs text-left" style={{ color: '#856404', lineHeight: 1.5 }}>
                <strong>Do not press back, refresh or close this page.</strong> Your order is being processed securely.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-8 py-6 md:py-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-dark-brown mb-5 md:mb-8">Checkout</h1>
        <form onSubmit={handlePlaceOrder} style={loading ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <div className="grid lg:grid-cols-3 gap-5 md:gap-8">
            <div className="lg:col-span-2 space-y-4 md:space-y-6 order-1">
              {/* Shipping */}
              <div className="bg-white rounded-xl border border-border-custom p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4 md:mb-6"><MapPin className="w-5 h-5 text-rust" /><h2 className="font-heading text-lg md:text-xl font-bold text-dark-brown">Shipping Address</h2></div>
                <div className="grid sm:grid-cols-2 gap-4">

                  {/* Full Name */}
                  <div className="sm:col-span-2">
                    <Label className="font-body text-sm font-medium text-dark-brown">Full Name *</Label>
                    <Input value={address.name} onChange={e => setAddress({...address, name: e.target.value})} placeholder="Enter your full name" className="mt-1 bg-cream/50" data-testid="checkout-name" required />
                  </div>

                  {/* Address Line 1 */}
                  <div className="sm:col-span-2">
                    <Label className="font-body text-sm font-medium text-dark-brown">House / Flat / Building *</Label>
                    <Input value={address.line1} onChange={e => setAddress({...address, line1: e.target.value})} placeholder="House no., Building name, Street" className="mt-1 bg-cream/50" data-testid="checkout-address1" required />
                  </div>

                  {/* Address Line 2 */}
                  <div className="sm:col-span-2">
                    <Label className="font-body text-sm font-medium text-dark-brown">Area / Locality / Landmark</Label>
                    <Input value={address.line2} onChange={e => setAddress({...address, line2: e.target.value})} placeholder="Area, Colony, Mohalla (optional)" className="mt-1 bg-cream/50" />
                  </div>

                  {/* PIN Code with auto-lookup */}
                  <div className="sm:col-span-2">
                    <Label className="font-body text-sm font-medium text-dark-brown">PIN Code *</Label>
                    <div className="relative mt-1">
                      <Input
                        value={address.pincode}
                        onChange={handlePincodeChange}
                        placeholder="6-digit PIN code"
                        inputMode="numeric"
                        maxLength={6}
                        className={`bg-cream/50 pr-10 ${
                          pincodeStatus === 'error' ? 'border-red-400 focus:ring-red-300' :
                          pincodeStatus === 'success' ? 'border-green-400 focus:ring-green-300' : ''
                        }`}
                        data-testid="checkout-pincode"
                        required
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {pincodeStatus === 'loading' && <Loader2 className="w-4 h-4 text-rust animate-spin" />}
                        {pincodeStatus === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        {pincodeStatus === 'error' && <AlertCircle className="w-4 h-4 text-red-400" />}
                      </div>
                    </div>
                    {pincodeStatus === 'error' && <p className="font-body text-xs text-red-500 mt-1">{pincodeError}</p>}
                    {pincodeStatus === 'success' && postOffices.length > 0 && (
                      <p className="font-body text-xs text-green-600 mt-1">✓ Verified — {postOffices.slice(0,3).join(', ')}{postOffices.length > 3 ? ` +${postOffices.length - 3} more` : ''}</p>
                    )}
                  </div>

                  {/* City — auto-filled */}
                  <div>
                    <Label className="font-body text-sm font-medium text-dark-brown">City / District *</Label>
                    <Input
                      value={address.city}
                      onChange={e => setAddress({...address, city: e.target.value})}
                      placeholder={pincodeStatus === 'loading' ? 'Fetching…' : 'Auto-filled from PIN'}
                      className={`mt-1 bg-cream/50 ${pincodeStatus === 'success' ? 'bg-green-50 text-dark-brown' : ''}`}
                      readOnly={pincodeStatus === 'success'}
                      required
                    />
                  </div>

                  {/* State — auto-filled */}
                  <div>
                    <Label className="font-body text-sm font-medium text-dark-brown">State *</Label>
                    <Input
                      value={address.state}
                      onChange={e => setAddress({...address, state: e.target.value})}
                      placeholder={pincodeStatus === 'loading' ? 'Fetching…' : 'Auto-filled from PIN'}
                      className={`mt-1 bg-cream/50 ${pincodeStatus === 'success' ? 'bg-green-50 text-dark-brown' : ''}`}
                      readOnly={pincodeStatus === 'success'}
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="sm:col-span-2">
                    <Label className="font-body text-sm font-medium text-dark-brown">Mobile Number *</Label>
                    <div className="flex mt-1">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-cream text-mid-brown font-body text-sm">+91</span>
                      <Input
                        value={address.phone}
                        onChange={handlePhoneChange}
                        placeholder="10-digit mobile number"
                        inputMode="numeric"
                        maxLength={10}
                        className={`rounded-l-none bg-cream/50 ${
                          phoneError ? 'border-red-400 focus:ring-red-300' :
                          (address.phone.length === 10 && !phoneError) ? 'border-green-400' : ''
                        }`}
                        data-testid="checkout-phone"
                        required
                      />
                    </div>
                    {phoneError && <p className="font-body text-xs text-red-500 mt-1">{phoneError}</p>}
                    {address.phone.length === 10 && !phoneError && <p className="font-body text-xs text-green-600 mt-1">✓ Valid mobile number</p>}
                  </div>

                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl border border-border-custom p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4"><CreditCard className="w-5 h-5 text-rust" /><h2 className="font-heading text-lg md:text-xl font-bold text-dark-brown">Payment Method</h2></div>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'razorpay' ? 'border-rust bg-rust/5' : 'border-border-custom hover:border-rust/30'}`}>
                    <input type="radio" name="payment" value="razorpay" checked={paymentMethod === 'razorpay'} onChange={() => setPaymentMethod('razorpay')} className="accent-rust" data-testid="payment-razorpay" />
                    <CreditCard className="w-5 h-5 text-rust" />
                    <div><div className="font-body font-medium text-dark-brown text-sm">Pay Online (Razorpay)</div><div className="font-body text-xs text-light-brown">UPI, Cards, Net Banking</div></div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${!codAvailable ? 'opacity-50 cursor-not-allowed' : ''} ${paymentMethod === 'cod' ? 'border-green-500 bg-green-50' : 'border-border-custom hover:border-green-300'}`}>
                    <input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={() => codAvailable && setPaymentMethod('cod')} disabled={!codAvailable} className="accent-green-600" data-testid="payment-cod" />
                    <Banknote className="w-5 h-5 text-green-600" />
                    <div><div className="font-body font-medium text-dark-brown text-sm">Cash on Delivery</div><div className="font-body text-xs text-light-brown">{codAvailable ? 'Pay when you receive' : 'Not available for some items'}</div></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl border border-border-custom p-4 md:p-6 h-fit lg:sticky lg:top-24 order-2">
              <h3 className="font-heading text-lg md:text-xl font-bold text-dark-brown mb-3 md:mb-4">Order Summary</h3>
              <div className="space-y-3 mb-4">
                {cart.items.map(item => (
                  <div key={item.product_id} className="flex justify-between text-sm font-body">
                    <span className="text-light-brown truncate pr-2">{item.product?.name} x{item.quantity}</span>
                    <span className="text-dark-brown font-medium shrink-0">{'\u20B9'}{((item.product?.price || 0) * item.quantity).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border-custom pt-3 mb-6">
                <div className="flex justify-between font-body text-sm mb-1"><span className="text-light-brown">Subtotal</span><span className="text-dark-brown">{'\u20B9'}{cart.total.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between font-body text-sm mb-1"><span className="text-light-brown">Shipping</span><span className="text-green-600">Free</span></div>
                <div className="border-t border-border-custom pt-2 mt-2 flex justify-between"><span className="font-body font-semibold text-dark-brown">Total to Pay</span><span className="font-body text-xl font-bold text-rust" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }} data-testid="checkout-total">{'\u20B9'}{cart.total.toLocaleString('en-IN', {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-12" data-testid="place-order-btn">
                {loading ? <span className="spinner mr-2"></span> : paymentMethod === 'cod' ? <Banknote className="w-4 h-4 mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                {paymentMethod === 'cod' ? 'Place COD Order' : 'Pay & Place Order'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
